/* @jsx h */
import { useLocalization } from "@lemmonhq/uikit";
import { defineComponent, h } from "vue";
import type { SetupError } from "./bluetoothPrinter";
import { StepHeader } from "./PrinterSetupLayout";
import { HeroIcon, SetupErrorMessage } from "./PrinterSetupSteps";

export type ExistingPrinter = {
  id: number;
  name: string;
  ip: string | null;
  serial_number: string;
};

export type RegisterForm = { name: string; ip: string };

export type RegisterFormError = "name_required" | "ip_invalid";

const DuplicateWarning = defineComponent<{
  serialNumber: string;
  printers: ExistingPrinter[];
}>({
  name: "PrinterRegisterDuplicateWarning",
  props: ["serialNumber", "printers"],
  setup(props) {
    const { t } = useLocalization();
    return () => (
      <div class="mt-4 w-full max-w-sm rounded-2xl border border-warning/30 bg-warning/10 p-4 text-left text-sm">
        <div class="flex gap-2 text-warning">
          <span class="material-symbols-outlined">warning</span>
          <span>
            {t("devices.bt_printer.register_duplicate").replace(
              "{serial}",
              props.serialNumber,
            )}
          </span>
        </div>
        <ul class="mt-2 space-y-1 text-xs text-base-content/70">
          {props.printers.map((printer) => (
            <li key={printer.id} class="flex justify-between gap-2">
              <span class="truncate">{printer.name}</span>
              <span class="font-mono">{printer.ip || "-"}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  },
});

export const RegisterStep = defineComponent<{
  form: RegisterForm;
  formError: RegisterFormError | null;
  isIpAutofilled: boolean;
  serialNumber: string;
  isCheckingSerial: boolean;
  duplicates: ExistingPrinter[];
  disabled: boolean;
  error: SetupError | null;
  onChange: (form: RegisterForm) => void;
  onSubmit: () => void;
}>({
  name: "PrinterSetupRegisterStep",
  props: [
    "form",
    "formError",
    "isIpAutofilled",
    "serialNumber",
    "isCheckingSerial",
    "duplicates",
    "disabled",
    "error",
    "onChange",
    "onSubmit",
  ],
  setup(props) {
    const { t } = useLocalization();
    const update = (patch: Partial<RegisterForm>) =>
      props.onChange({ ...props.form, ...patch });

    return () => (
      <div class="flex flex-col items-center justify-center px-4 pb-4 pt-2 text-center">
        <HeroIcon icon="add_circle" isAnimating={props.disabled} />
        <StepHeader
          title={t("devices.bt_printer.register_title")}
          text={t(
            props.isIpAutofilled
              ? "devices.bt_printer.register_desc_manual"
              : "devices.bt_printer.register_desc",
          )}
        />
        <form
          class="w-full max-w-sm space-y-2.5 text-left"
          onSubmit={(event: Event) => {
            event.preventDefault();
            props.onSubmit();
          }}
        >
          <label class="form-control w-full">
            <span class="label-text mb-1">
              {t("devices.bt_printer.register_name_label")}
            </span>
            <input
              type="text"
              class="input input-bordered h-10 min-h-10 w-full"
              value={props.form.name}
              disabled={props.disabled}
              onInput={(event: Event) =>
                update({ name: (event.target as HTMLInputElement).value })
              }
            />
          </label>
          <label class="form-control w-full">
            <span class="label-text mb-1">
              {t("devices.bt_printer.ip_address_label")}
            </span>
            <input
              type="text"
              inputmode="decimal"
              autocomplete="off"
              placeholder="192.168.1.50"
              class="input input-bordered h-10 min-h-10 w-full font-mono"
              value={props.form.ip}
              disabled={props.disabled}
              onInput={(event: Event) =>
                update({ ip: (event.target as HTMLInputElement).value })
              }
            />
          </label>
          {props.serialNumber && (
            <p class="text-xs text-base-content/50">
              {t("devices.bt_printer.register_serial").replace(
                "{serial}",
                props.serialNumber,
              )}
            </p>
          )}
          {props.formError && (
            <p class="text-sm text-error">
              {t(`devices.bt_printer.register_${props.formError}`)}
            </p>
          )}
        </form>
        {props.isCheckingSerial && (
          <p class="mt-4 flex items-center gap-2 text-xs text-base-content/50">
            <span class="loading loading-spinner loading-xs" />
            {t("devices.bt_printer.register_checking")}
          </p>
        )}
        {props.duplicates.length > 0 && (
          <DuplicateWarning
            serialNumber={props.serialNumber}
            printers={props.duplicates}
          />
        )}
        {props.error && <SetupErrorMessage error={props.error} />}
      </div>
    );
  },
});
